import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, PauseIcon, SkipForwardIcon, SkipBackIcon, Volume2Icon, VolumeXIcon, MusicIcon, ListMusicIcon, ShuffleIcon, RepeatIcon, HeartIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface Track {
  id: number;
  title: string;
  artist: string;
  duration: string;
  durationSec: number;
}
interface Playlist {
  id: number;
  name: string;
  description: string;
  color: string;
  trackCount: number;
  tracks: Track[];
}
const playlists: Playlist[] = [{
  id: 1,
  name: 'Executive Calm',
  description: 'Focus & clarity',
  color: '#C9A84C',
  trackCount: 12,
  tracks: [{
    id: 1,
    title: 'Weightless',
    artist: 'Marconi Union',
    duration: '8:09',
    durationSec: 489
  }, {
    id: 2,
    title: 'Experience',
    artist: 'Ludovico Einaudi',
    duration: '5:14',
    durationSec: 314
  }, {
    id: 3,
    title: 'Clair de Lune',
    artist: 'Claude Debussy',
    duration: '5:02',
    durationSec: 302
  }, {
    id: 4,
    title: 'Gymnopédie No.1',
    artist: 'Erik Satie',
    duration: '3:05',
    durationSec: 185
  }]
}, {
  id: 2,
  name: 'Boardroom Jazz',
  description: 'Sophisticated ambience',
  color: '#60A5FA',
  trackCount: 15,
  tracks: [{
    id: 5,
    title: 'So What',
    artist: 'Miles Davis',
    duration: '9:22',
    durationSec: 562
  }, {
    id: 6,
    title: 'Take Five',
    artist: 'Dave Brubeck',
    duration: '5:24',
    durationSec: 324
  }, {
    id: 7,
    title: 'Autumn Leaves',
    artist: 'Bill Evans',
    duration: '7:03',
    durationSec: 423
  }, {
    id: 8,
    title: 'Blue in Green',
    artist: 'Miles Davis',
    duration: '5:37',
    durationSec: 337
  }]
}, {
  id: 3,
  name: 'Visitor Welcome',
  description: 'Warm & inviting',
  color: '#34D399',
  trackCount: 10,
  tracks: [{
    id: 9,
    title: 'Bittersweet Symphony',
    artist: 'The Verve',
    duration: '5:57',
    durationSec: 357
  }, {
    id: 10,
    title: 'Clocks',
    artist: 'Coldplay',
    duration: '5:09',
    durationSec: 309
  }, {
    id: 11,
    title: 'The Scientist',
    artist: 'Coldplay',
    duration: '5:09',
    durationSec: 309
  }, {
    id: 12,
    title: 'Fix You',
    artist: 'Coldplay',
    duration: '4:55',
    durationSec: 295
  }]
}, {
  id: 4,
  name: 'Indian Classical',
  description: 'Heritage & culture',
  color: '#F472B6',
  trackCount: 8,
  tracks: [{
    id: 13,
    title: 'Raga Bhairav',
    artist: 'Pandit Ravi Shankar',
    duration: '12:30',
    durationSec: 750
  }, {
    id: 14,
    title: 'Raag Yaman',
    artist: 'Ustad Vilayat Khan',
    duration: '18:45',
    durationSec: 1125
  }, {
    id: 15,
    title: 'Mishra Khamaj',
    artist: 'Pandit Hariprasad Chaurasia',
    duration: '14:20',
    durationSec: 860
  }, {
    id: 16,
    title: 'Raga Desh',
    artist: 'Ustad Amjad Ali Khan',
    duration: '16:10',
    durationSec: 970
  }]
}];
export function MusicPlayer() {
  const [activePlaylist, setActivePlaylist] = useState<Playlist>(playlists[0]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(65);
  const [progress, setProgress] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [liked, setLiked] = useState<Set<number>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTrack = activePlaylist.tracks[currentTrackIndex];
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            handleNext();
            return 0;
          }
          return prev + 100 / currentTrack.durationSec * 0.5;
        });
      }, 500);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, currentTrackIndex, activePlaylist]);
  const handleNext = () => {
    setCurrentTrackIndex(prev => (prev + 1) % activePlaylist.tracks.length);
    setProgress(0);
  };
  const handlePrev = () => {
    setCurrentTrackIndex(prev => (prev - 1 + activePlaylist.tracks.length) % activePlaylist.tracks.length);
    setProgress(0);
  };
  const handlePlaylistChange = (playlist: Playlist) => {
    setActivePlaylist(playlist);
    setCurrentTrackIndex(0);
    setProgress(0);
    setIsPlaying(true);
  };
  const toggleLike = (id: number) => {
    setLiked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const formatProgress = (pct: number) => {
    const secs = Math.floor(pct / 100 * currentTrack.durationSec);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.25
  }} className="rounded-2xl overflow-hidden flex flex-col" style={{
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(201,168,76,0.2)'
  }} data-id="element-217">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[rgba(201,168,76,0.12)]" data-id="element-218">
        <div className="flex items-center gap-3" data-id="element-219">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{
          background: 'linear-gradient(135deg, #fc3c44, #ff6b6b)'
        }} data-id="element-220">
            <MusicIcon className="w-4 h-4 text-white" data-id="element-221" />
          </div>
          <div data-id="element-222">
            <h3 className="text-base font-semibold text-white" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-223">
              Apple Music
            </h3>
            <p className="text-xs text-gray-400" data-id="element-224">Curated for your guests</p>
          </div>
        </div>
        <button onClick={() => setIsExpanded(!isExpanded)} className="w-8 h-8 rounded-lg bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] flex items-center justify-center transition-colors" data-id="element-225">
          {isExpanded ? <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-226" /> : <ChevronUpIcon className="w-4 h-4 text-gray-400" data-id="element-227" />}
        </button>
      </div>

      {/* Playlist Selector */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto border-b border-[rgba(255,255,255,0.05)]" data-id="element-228">
        {playlists.map(pl => <button key={pl.id} onClick={() => handlePlaylistChange(pl)} className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all ${activePlaylist.id === pl.id ? 'text-white' : 'bg-[rgba(255,255,255,0.04)] text-gray-400 hover:bg-[rgba(255,255,255,0.08)]'}`} style={activePlaylist.id === pl.id ? {
        backgroundColor: `${pl.color}20`,
        border: `1px solid ${pl.color}40`,
        color: pl.color
      } : {}} data-id="element-229">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
          backgroundColor: pl.color
        }} data-id="element-230" />
            {pl.name}
          </button>)}
      </div>

      {/* Now Playing */}
      <div className="px-5 py-4" data-id="element-231">
        <div className="flex items-center gap-4 mb-4" data-id="element-232">
          {/* Album art placeholder */}
          <div className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center shadow-lg" style={{
          background: `linear-gradient(135deg, ${activePlaylist.color}40, ${activePlaylist.color}15)`,
          border: `1px solid ${activePlaylist.color}30`
        }} data-id="element-233">
            <MusicIcon className="w-6 h-6" style={{
            color: activePlaylist.color
          }} data-id="element-234" />
          </div>

          <div className="flex-1 min-w-0" data-id="element-235">
            <p className="text-sm font-semibold text-white truncate" data-id="element-236">
              {currentTrack.title}
            </p>
            <p className="text-xs text-gray-400 truncate mt-0.5" data-id="element-237">
              {currentTrack.artist}
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-id="element-238">
              {activePlaylist.name}
            </p>
          </div>

          <button onClick={() => toggleLike(currentTrack.id)} className="w-9 h-9 flex items-center justify-center transition-colors" data-id="element-239">
            <HeartIcon className="w-5 h-5 transition-colors" style={{
            color: liked.has(currentTrack.id) ? '#fc3c44' : '#6B7280'
          }} fill={liked.has(currentTrack.id) ? '#fc3c44' : 'none'} data-id="element-240" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-3" data-id="element-241">
          <div className="w-full h-1.5 rounded-full bg-[rgba(255,255,255,0.08)] cursor-pointer overflow-hidden" onClick={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          setProgress((e.clientX - rect.left) / rect.width * 100);
        }} data-id="element-242">
            <motion.div className="h-full rounded-full" style={{
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${activePlaylist.color}, ${activePlaylist.color}cc)`
          }} data-id="element-243" />
          </div>
          <div className="flex justify-between mt-1.5 text-xs text-gray-500" data-id="element-244">
            <span data-id="element-245">{formatProgress(progress)}</span>
            <span data-id="element-246">{currentTrack.duration}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between" data-id="element-247">
          <button className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-gray-300 transition-colors" data-id="element-248">
            <ShuffleIcon className="w-4 h-4" data-id="element-249" />
          </button>
          <button onClick={handlePrev} className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-white transition-colors" data-id="element-250">
            <SkipBackIcon className="w-5 h-5" data-id="element-251" />
          </button>
          <button onClick={() => setIsPlaying(!isPlaying)} className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105" style={{
          background: `linear-gradient(135deg, ${activePlaylist.color}, ${activePlaylist.color}cc)`
        }} data-id="element-252">
            {isPlaying ? <PauseIcon className="w-6 h-6 text-[#0D0F14]" data-id="element-253" /> : <PlayIcon className="w-6 h-6 text-[#0D0F14] ml-0.5" data-id="element-254" />}
          </button>
          <button onClick={handleNext} className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-white transition-colors" data-id="element-255">
            <SkipForwardIcon className="w-5 h-5" data-id="element-256" />
          </button>
          <button className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-gray-300 transition-colors" data-id="element-257">
            <RepeatIcon className="w-4 h-4" data-id="element-258" />
          </button>
        </div>

        {/* Volume */}
        <div className="flex items-center gap-3 mt-4" data-id="element-259">
          <button onClick={() => setIsMuted(!isMuted)} className="text-gray-500 hover:text-gray-300 transition-colors" data-id="element-260">
            {isMuted ? <VolumeXIcon className="w-4 h-4" data-id="element-261" /> : <Volume2Icon className="w-4 h-4" data-id="element-262" />}
          </button>
          <div className="flex-1 h-1.5 rounded-full bg-[rgba(255,255,255,0.08)] cursor-pointer overflow-hidden" onClick={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          setVolume(Math.round((e.clientX - rect.left) / rect.width * 100));
        }} data-id="element-263">
            <div className="h-full rounded-full bg-[rgba(255,255,255,0.3)]" style={{
            width: `${isMuted ? 0 : volume}%`
          }} data-id="element-264" />
          </div>
          <span className="text-xs text-gray-500 w-7 text-right" data-id="element-265">
            {isMuted ? 0 : volume}
          </span>
        </div>
      </div>

      {/* Track List (expanded) */}
      <AnimatePresence data-id="element-266">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden border-t border-[rgba(255,255,255,0.05)]" data-id="element-267">
            <div className="px-4 py-3 flex items-center gap-2 mb-1" data-id="element-268">
              <ListMusicIcon className="w-4 h-4 text-gray-500" data-id="element-269" />
              <span className="text-xs text-gray-400 font-medium" data-id="element-270">Up Next</span>
            </div>
            {activePlaylist.tracks.map((track, i) => <button key={track.id} onClick={() => {
          setCurrentTrackIndex(i);
          setProgress(0);
          setIsPlaying(true);
        }} className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[rgba(255,255,255,0.04)] transition-colors text-left ${i === currentTrackIndex ? 'bg-[rgba(201,168,76,0.06)]' : ''}`} data-id="element-271">
                <span className="w-5 text-xs text-gray-600 text-center" data-id="element-272">
                  {i === currentTrackIndex && isPlaying ? <span style={{
              color: activePlaylist.color
            }} data-id="element-273">
                      ♪
                    </span> : i + 1}
                </span>
                <div className="flex-1 min-w-0" data-id="element-274">
                  <p className={`text-xs font-medium truncate ${i === currentTrackIndex ? 'text-white' : 'text-gray-300'}`} data-id="element-275">
                    {track.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-276">
                    {track.artist}
                  </p>
                </div>
                <span className="text-xs text-gray-600 flex-shrink-0" data-id="element-277">
                  {track.duration}
                </span>
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}